
export const projectTitle = "OASI on the Street"
export const APIurl = /*!process.env.NODE_ENV || process.env.NODE_ENV === 'development' ? 'http://localhost:8808' :*/ 'https://api.mosaicorefugees.org'
export const SUPER_ADMIN_EMAIL = "danieledigregorio8@gmail.com"

// Messaggi in italiano per i codici di errore Firebase Auth più comuni.
// Non mostrare mai il messaggio grezzo di Firebase all'utente: rivela lo stack tecnologico.
const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
    "auth/invalid-email": "Indirizzo email non valido.",
    "auth/user-disabled": "Questo account è stato disabilitato.",
    "auth/user-not-found": "Email o password non corretti.",
    "auth/wrong-password": "Email o password non corretti.",
    "auth/invalid-credential": "Email o password non corretti.",
    "auth/invalid-login-credentials": "Email o password non corretti.",
    "auth/missing-password": "Inserisci una password.",
    "auth/missing-email": "Inserisci un indirizzo email.",
    "auth/too-many-requests": "Troppi tentativi. Riprova più tardi.",
    "auth/network-request-failed": "Errore di connessione. Controlla la rete e riprova.",
    "auth/popup-closed-by-user": "Accesso annullato.",
    "auth/cancelled-popup-request": "Accesso annullato.",
    "auth/popup-blocked": "Il popup di accesso è stato bloccato dal browser. Abilita i popup e riprova.",
    "auth/account-exists-with-different-credential": "Esiste già un account con questa email ma con un metodo di accesso diverso.",
    "auth/operation-not-allowed": "Questo metodo di accesso non è abilitato.",
    "auth/weak-password": "La password è troppo debole.",
    "auth/email-already-in-use": "Questa email è già in uso.",
    "auth/requires-recent-login": "Per motivi di sicurezza devi effettuare nuovamente l'accesso.",
    "auth/expired-action-code": "Il link utilizzato è scaduto.",
    "auth/invalid-action-code": "Il link utilizzato non è valido.",
    "auth/user-token-expired": "La sessione è scaduta. Accedi di nuovo.",
    "auth/unauthorized-domain": "Dominio non autorizzato per l'accesso.",
}

const GENERIC_ERROR_MESSAGE = "Si è verificato un errore imprevisto. Riprova."

// Converte qualsiasi errore (Firebase, rete, backend) in un messaggio sicuro e in italiano.
// Non deve mai trapelare testo tipo "Firebase: ..." o codici interni all'utente finale.
export function getErrorMessage(err: unknown): string {
    if (!err) return GENERIC_ERROR_MESSAGE

    const code = (err as { code?: string })?.code
    if (typeof code === "string" && code.startsWith("auth/")) {
        return FIREBASE_ERROR_MESSAGES[code] ?? "Si è verificato un errore durante l'autenticazione. Riprova."
    }

    const rawMessage = (err as { message?: unknown })?.message
    const message = typeof rawMessage === "string" ? rawMessage : (typeof err === "string" ? err : "")

    if (message === "" || /firebase/i.test(message)) {
        return GENERIC_ERROR_MESSAGE
    }

    return message
}

export function hasChanges(obj1: any, obj2: any): boolean {
    // Stessa referenza o stesso valore primitivo
    if (obj1 === obj2) return false;

    // Uno è null/undefined e l'altro no
    if (obj1 == null || obj2 == null) return true;

    // Tipi diversi
    if (typeof obj1 !== typeof obj2) return true;

    // Date
    if (obj1 instanceof Date && obj2 instanceof Date) {
        return obj1.getTime() !== obj2.getTime();
    }

    // Array
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) return true;
        for (let i = 0; i < obj1.length; i++) {
            if (hasChanges(obj1[i], obj2[i])) return true;
        }
        return false;
    }

    // Oggetti plain
    if (typeof obj1 === "object" && typeof obj2 === "object") {
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);
        if (keys1.length !== keys2.length) return true;

        for (const key of keys1) {
            if (!keys2.includes(key)) return true;
            if (hasChanges(obj1[key], obj2[key])) return true;
        }
        return false;
    }

    // Primitivi (number, string, boolean, bigint, symbol, ecc.)
    return obj1 !== obj2;
}

export function convertNumbersToBooleans<T>(obj: T): T {
    if (obj === null || obj === undefined) return obj;

    if (Array.isArray(obj)) {
        return obj.map(item => convertNumbersToBooleans(item)) as any;
    }

    if (typeof obj === "object") {
        const result: any = {};
        for (const key in obj) {
            if (!obj.hasOwnProperty(key)) continue;
            const val = (obj as any)[key];

            if (val === 0) {
                result[key] = false;
            } else if (val === 1) {
                result[key] = true;
            } else if (typeof val === "object" && val !== null) {
                result[key] = convertNumbersToBooleans(val);
            } else {
                result[key] = val;
            }
        }
        return result;
    }

    // Valori primitivi non oggetti
    return obj;
}

// UTILS DOWNLOAD CSV

type CsvOptions<T> = {
    data: T[]
    fields?: string[]        // ora è string, così accetta "coordinates.lat"
    filename?: string
    filter?: (item: T) => boolean
    separator?: string
}

export function downloadCSV<T>({
                                   data,
                                   fields,
                                   filename = "export.csv",
                                   filter,
                                   separator = ",",
                               }: CsvOptions<T>) {
    const rows = filter ? data.filter(filter) : data
    if (!rows.length) return

    const firstRow = rows[0] as Record<string, unknown>
    const effectiveFields: string[] =
        fields && fields.length
            ? fields
            : Object.keys(firstRow)

    const header = effectiveFields

    const body = rows.map(row =>
        effectiveFields.map(path => {
            const value = getByPath(row, path)
            const str = value == null ? "" : String(value)
            const escaped = str.replace(/"/g, '""')
            return `"${escaped}"`
        })
    )

    const csvContent =
        [header, ...body]
            .map(cols => cols.join(separator))
            .join("\n")

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

export function downloadJSON(data: unknown, filename: string) {
    const jsonContent = JSON.stringify(data, null, 2)
    const blob = new Blob([jsonContent], { type: "application/json;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

function getByPath(obj: unknown, path: string): unknown {
    return path.split(".").reduce((acc: any, key) => {
        if (acc == null) return undefined
        return acc[key]
    }, obj as any)
}

export const isMobileDevice = () => {
    if (typeof navigator === "undefined") return false // lato server
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
}